import { Injectable } from '@angular/core'
import { getDaysInPeriod, getFirstDayOfUTCMonth, getYearMonthString } from '../utils/payroll.utils'
import {
  EmployeeSalary,
  ExtendedSalaryComponent
} from '../store/employee-salary/employee-salary.state'
import {
  PayrollCalculationBreakdown,
  PayrollCalculationContext,
  PayrollCalculationResult,
  SalaryComponent
} from '../store/payroll/payroll.state'

@Injectable({ providedIn: 'root' })
export class PayrollCalculationService {
  private monthlyTdsThreshold = 106250 // According to new tax regime in INR
  private round(value: number): number {
    return parseFloat(value.toFixed(2))
  }

  /**
   * Main orchestrator for calculating all payroll metrics for a single employee.
   */
  public calculatePayrollMetrics(ctx: PayrollCalculationContext): PayrollCalculationResult {
    const {
      currentSalary,
      previousSalary,
      payPeriodStart,
      payPeriodEnd,
      lopDays,
      lopDaysForArrearsPeriod,
      bonus,
      expenses,
      adjustments,
      rounding
    } = ctx

    const payPeriodStartDateObj = new Date(payPeriodStart + 'T00:00:00Z')
    const payPeriodEndDateObj = new Date(payPeriodEnd + 'T00:00:00Z')
    const daysInCurrentPayPeriod = getDaysInPeriod(payPeriodStartDateObj, payPeriodEndDateObj)

    const currentSalaryEffectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')
    const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)
    const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)

    const isArrearsCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth

    const isProrationCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
      currentSalaryEffectiveFromObj <= payPeriodEndDateObj

    let calculatedArrearsAmount = 0
    let breakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }

    const lopProrationFactor =
      daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0

    if (isArrearsCase && previousSalary) {
      calculatedArrearsAmount = this.calculateArrears(
        currentSalary,
        previousSalary,
        firstDayOfCurrentPayPeriodMonth,
        lopDaysForArrearsPeriod
      )
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd
      )
    } else if (isProrationCase && previousSalary) {
      breakdown = this.calculateProratedSalary(
        currentSalary,
        previousSalary,
        payPeriodStartDateObj,
        payPeriodEndDateObj,
        lopProrationFactor
      )
    } else {
      // Standard case or case where previous salary is not available
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd
      )
    }

    // --- Final Assembly and Total Calculation ---

    // Add contextual earnings to the breakdown
    if (calculatedArrearsAmount > 0) {
      breakdown.earnings.push({ name: 'Arrears', amount: this.round(calculatedArrearsAmount) })
    }
    if (bonus > 0) {
      breakdown.earnings.push({ name: 'Bonus', amount: this.round(bonus) })
    }
    if (expenses > 0) {
      breakdown.earnings.push({ name: 'Expenses Reimbursed', amount: this.round(expenses) })
    }
    if (adjustments !== 0) {
      // Adjustments can be positive (earning) or negative (deduction)
      if (adjustments > 0) {
        breakdown.earnings.push({ name: 'Other Adjustments', amount: this.round(adjustments) })
      } else {
        breakdown.deductions.push({
          name: 'Other Adjustments',
          amount: this.round(Math.abs(adjustments))
        })
      }
    }

    // Calculate totals FROM the breakdown for consistency
    const grossFromSalary = this.round(
      breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    )

    const tdsAfterCess = this.calculateTds(grossFromSalary)
    if (tdsAfterCess > 0) {
      breakdown.deductions.push({ name: 'TDS (Tax Deducted at Source)', amount: tdsAfterCess })
    }

    //const totalDeductions = breakdown.deductions.reduce((sum, comp) => sum + comp.amount, 0)
    //const netSalaryBeforeRounding = parseFloat((grossFromSalary - totalDeductions).toFixed(2))

    if (rounding !== 0) {
      // Add rounding as a specific line item for clarity on the payslip
      if (rounding > 0) {
        breakdown.earnings.push({ name: 'Rounding Adjustment', amount: this.round(rounding) })
      } else {
        breakdown.deductions.push({
          name: 'Rounding Adjustment',
          amount: this.round(Math.abs(rounding))
        })
      }
    }
    // let finalamount = 0
    // if (lopDays !== 0) {
    //   finalamount = (currentSalary.ctcValue / 12) * (1 - lopProrationFactor)
    //   breakdown.deductions.push({
    //     name: `LOP Deduction (for ${lopDays} Day${lopDays > 1 ? 's' : ''})`,
    //     amount: this.round(finalamount)
    //   })
    // }

    // Recalculate final totals after rounding is added to the breakdown
    const finalGrossSalary = this.round(
      breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    )
    const payPeriodStartDate = new Date(payPeriodStart) // convert string to Date
    if (
      finalGrossSalary >= 0 &&
      finalGrossSalary <= 21000 &&
      payPeriodStartDate >= new Date('2025-11-01')
    ) {
      const esiAmount = this.round(finalGrossSalary * 0.0075) // 0.75%

      breakdown.deductions.push({
        name: 'ESI',
        amount: esiAmount
      })
    }

    const finalTotalDeductions = this.round(
      breakdown.deductions.reduce((sum, comp) => sum + comp.amount, 0)
    )
    const netSalary = this.round(finalGrossSalary - finalTotalDeductions)

    return {
      grossSalary: Math.max(0, finalGrossSalary),
      netSalary,
      tdsAfterCess,
      arrearsAmountDisplay: calculatedArrearsAmount,
      calculationBreakdown: breakdown
    }
  }

  public calculateGrossMetrics(
    ctx: PayrollCalculationContext,
    employeeJoiningDate: string | Date | null,
    salaryHoldFroms: string | Date | null | undefined,
    salaryHoldTos: string | Date | null | undefined,
    salaryHold: boolean
  ): {
    grossSalary: number
    calculationBreakdown: PayrollCalculationBreakdown
    netSalary: number
  } {
    const {
      currentSalary,
      previousSalary,
      payPeriodStart,
      payPeriodEnd,
      lopDays,
      lopDaysForArrearsPeriod,
      bonus,
      expenses,
      adjustments,
      tdsAfterCess,
      salaryHoldFrom,
      salaryHoldTo,
      esi
    } = ctx
    let gpsPayPeriodStart: any = ''
    if (employeeJoiningDate) {
      gpsPayPeriodStart =
        employeeJoiningDate >= payPeriodStart ? employeeJoiningDate : payPeriodStart
    }

    const payPeriodStartDateObj = new Date(gpsPayPeriodStart + 'T00:00:00Z')
    // const payPeriodStartDateObj = new Date(
    //   employeeJoiningDate ? employeeJoiningDate + 'T00:00:00Z' : payPeriodStart + 'T00:00:00Z'
    // )
    const payPeriodEndDateObj = new Date(payPeriodEnd + 'T00:00:00Z')

    const daysInCurrentPayPeriod = getDaysInPeriod(payPeriodStartDateObj, payPeriodEndDateObj)

    const currentSalaryEffectiveFromObj = new Date(currentSalary.effectiveFrom + 'T00:00:00Z')
    const firstDayOfCurrentPayPeriodMonth = getFirstDayOfUTCMonth(payPeriodStartDateObj)
    const payrollMonthYearStr = getYearMonthString(payPeriodStartDateObj)
    const holdFromStr = salaryHoldFrom ? salaryHoldFrom : salaryHoldFroms ? salaryHoldFroms : null

    const holdFromDate = holdFromStr ? new Date(holdFromStr) : null
    const holdToStr = salaryHoldTo ? salaryHoldTo : salaryHoldTos ? salaryHoldTos : null
    const holdToDate = holdToStr ? new Date(holdToStr) : null

    // 2. Process salary hold
    // const holdFromDate = salaryHoldFrom ? new Date(salaryHoldFrom) : null
    // const holdToDate = salaryHoldTo ? new Date(salaryHoldTo) : null
    let workedDays = daysInCurrentPayPeriod

    // if (
    //   holdFromDate &&
    //   holdFromDate >= payPeriodStartDateObj &&
    //   holdFromDate <= payPeriodEndDateObj
    // ) {
    //   workedDays = holdFromDate.getDate() - payPeriodStartDateObj.getDate()
    // }

    if (holdFromDate && holdToDate && salaryHold) {
      const daysBeforeHold = holdFromDate.getDate() - payPeriodStartDateObj.getDate()
      // const daysAfterHold = payPeriodEndDateObj.getDate() - holdToDate.getDate()
      // workedDays = daysBeforeHold + daysAfterHold
      workedDays = daysBeforeHold
    }
    let lopProrationFactor = 0
    if (salaryHold && (salaryHoldFrom || salaryHoldFroms)) {
      workedDays = Math.max(0, Math.min(workedDays, daysInCurrentPayPeriod))
      lopProrationFactor =
        daysInCurrentPayPeriod > 0 ? (workedDays - lopDays) / daysInCurrentPayPeriod : 0
    } else {
      lopProrationFactor =
        daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0
    }
    // console.log('lopProrationFactor', lopProrationFactor)
    const isArrearsCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj < firstDayOfCurrentPayPeriodMonth

    const isProrationCase =
      !!previousSalary &&
      currentSalary.payoutFrom === payrollMonthYearStr &&
      currentSalaryEffectiveFromObj > firstDayOfCurrentPayPeriodMonth &&
      currentSalaryEffectiveFromObj <= payPeriodEndDateObj

    let calculatedArrearsAmount = 0
    let breakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }

    // const lopProrationFactor =
    //   daysInCurrentPayPeriod > 0 ? (daysInCurrentPayPeriod - lopDays) / daysInCurrentPayPeriod : 0

    if (isArrearsCase && previousSalary) {
      calculatedArrearsAmount = this.calculateArrears(
        currentSalary,
        previousSalary,
        firstDayOfCurrentPayPeriodMonth,
        lopDaysForArrearsPeriod
      )
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        payPeriodStart,
        payPeriodEnd
      )
    } else if (isProrationCase && previousSalary) {
      breakdown = this.calculateProratedSalary(
        currentSalary,
        previousSalary,
        payPeriodStartDateObj,
        payPeriodEndDateObj,
        lopProrationFactor
      )
    } else {
      // Standard case or case where previous salary is not available
      breakdown = this.calculateMonthlySalaryComponents(
        currentSalary,
        lopProrationFactor,
        // salaryHoldFrom ? salaryHoldFrom.toString() : payPeriodStart,
        // salaryHoldTo ? salaryHoldTo.toString() : payPeriodEnd
        payPeriodStart,
        payPeriodEnd
      )
    }
    // --- Final Assembly and Total Calculation ---

    // Add contextual earnings to the breakdown
    if (calculatedArrearsAmount > 0) {
      breakdown.earnings.push({ name: 'Arrears', amount: this.round(calculatedArrearsAmount) })
    }
    if (bonus > 0) {
      breakdown.earnings.push({ name: 'Bonus', amount: this.round(bonus) })
    }
    if (expenses > 0) {
      breakdown.earnings.push({ name: 'Expenses Reimbursed', amount: this.round(expenses) })
    }
    if (adjustments !== 0) {
      // Adjustments can be positive (earning) or negative (deduction)
      if (adjustments > 0) {
        breakdown.earnings.push({ name: 'Other Adjustments', amount: this.round(adjustments) })
      } else {
        breakdown.deductions.push({
          name: 'Other Adjustments',
          amount: this.round(Math.abs(adjustments))
        })
      }
    }
    // if (tdsAfterCess && tdsAfterCess > 0) {
    //   breakdown.deductions.push({
    //     name: 'TDS',
    //     amount: this.round(Math.abs(tdsAfterCess))
    //   })
    // }

    // if (lopDays && lopDays > 0) {
    //   breakdown.deductions.push({
    //     name: 'LOP',
    //     amount: this.round(Math.abs(lopDays))
    //   })
    // }

    //// Calculate totals FROM the breakdown for consistency
    //const grossFromSalary = this.round(
    //  breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    //)

    // We can add rounding to the breakdown directly here before returning
    if (ctx.rounding !== 0) {
      if (ctx.rounding > 0) {
        breakdown.earnings.push({ name: 'Rounding Adjustment', amount: this.round(ctx.rounding) })
      } else {
        breakdown.deductions.push({
          name: 'Rounding Adjustment',
          amount: this.round(Math.abs(ctx.rounding))
        })
      }
    }
    // let finalamount = 0
    // if (lopDays !== 0) {
    //   finalamount = (currentSalary.ctcValue / 12) * (1 - lopProrationFactor)
    //   breakdown.deductions.push({
    //     name: `LOP Deduction (for ${lopDays} Day${lopDays > 1 ? 's' : ''})`,
    //     amount: this.round(finalamount)
    //   })
    // }

    // Recalculate final gross after rounding is added
    const finalGrossSalary = this.round(
      breakdown.earnings.reduce((sum, comp) => sum + comp.amount, 0)
    )
    const payPeriodStartDate = new Date(payPeriodStart) // convert string to Date
    payPeriodStartDate.setHours(0, 0, 0, 0)

    const esiStartDate = new Date(2025, 10, 1) // Nov = month 10
    esiStartDate.setHours(0, 0, 0, 0)
    let esiAmount = ctx.esi ?? 0
    console.log('finalgrosssalary', finalGrossSalary)
    if (
      finalGrossSalary >= 0 &&
      finalGrossSalary <= 21000 &&
      payPeriodStartDate >= esiStartDate &&
      ctx.esi === 0 &&
      ctx.currentSalary.statutoryCompliances.esi === true
    ) {
      // console.log('esi amount', esiAmount)
      esiAmount = this.round(finalGrossSalary * 0.0075) // 0.75%

      breakdown.deductions.push({
        name: 'ESI',
        amount: esiAmount
      })
    } else if (ctx.esi && ctx.esi > 0) {
      // esiAmount = this.round(finalGrossSalary * 0.0075) // 0.75%
      breakdown.deductions.push({
        name: 'ESI',
        amount: esiAmount
      })
    }
    let netSalary = finalGrossSalary - (tdsAfterCess ?? 0) - esiAmount
    if (ctx.rounding !== 0 && ctx.rounding < 0) {
      netSalary -= Math.abs(ctx.rounding)
    }
    // console.log('net salary', netSalary)
    return {
      grossSalary: Math.max(0, finalGrossSalary),
      calculationBreakdown: breakdown,
      netSalary: Math.max(0, netSalary)
    }
  }

  private calculateArrears(
    newSalary: EmployeeSalary,
    oldSalary: EmployeeSalary,
    firstDayOfCurrentPayPeriodMonth: Date,
    lopDaysForArrearsPeriod: number
  ): number {
    const arrearPeriodStartObj = new Date(newSalary.effectiveFrom + 'T00:00:00Z')
    const arrearPeriodEndObj = new Date(firstDayOfCurrentPayPeriodMonth.getTime())
    arrearPeriodEndObj.setUTCDate(arrearPeriodEndObj.getUTCDate() - 1)
    if (arrearPeriodStartObj > arrearPeriodEndObj) return 0

    const totalDaysInArrearPeriod = getDaysInPeriod(arrearPeriodStartObj, arrearPeriodEndObj)
    const payableDays = Math.max(0, totalDaysInArrearPeriod - lopDaysForArrearsPeriod)

    if (payableDays <= 0) return 0

    const dailyDifference = newSalary.ctcValue / 365 - oldSalary.ctcValue / 365
    return this.round(payableDays * dailyDifference)
  }

  private calculateProratedSalary(
    newSalary: EmployeeSalary,
    oldSalary: EmployeeSalary,
    payPeriodStart: Date,
    payPeriodEnd: Date,
    lopProrationFactor: number
  ) {
    const newSalaryEffectiveDate = new Date(newSalary.effectiveFrom + 'T00:00:00Z')
    const totalDaysInPayPeriod = getDaysInPeriod(payPeriodStart, payPeriodEnd)

    const daysWithOldSalary = getDaysInPeriod(
      payPeriodStart,
      new Date(newSalaryEffectiveDate.getTime() - 24 * 60 * 60 * 1000)
    )
    const daysWithNewSalary = getDaysInPeriod(newSalaryEffectiveDate, payPeriodEnd)

    const oldPartBreakdown = this.calculateProratedPortionForPeriod(
      oldSalary,
      daysWithOldSalary,
      totalDaysInPayPeriod,
      payPeriodStart.toISOString().split('T')[0],
      payPeriodEnd.toISOString().split('T')[0]
    )

    const newPartBreakdown = this.calculateProratedPortionForPeriod(
      newSalary,
      daysWithNewSalary,
      totalDaysInPayPeriod,
      payPeriodStart.toISOString().split('T')[0],
      payPeriodEnd.toISOString().split('T')[0]
    )

    const mergedBreakdown = this.mergeBreakdowns(oldPartBreakdown, newPartBreakdown)

    // Apply LOP factor to each component in the final merged breakdown
    mergedBreakdown.earnings.forEach(
      comp => (comp.amount = this.round(comp.amount * lopProrationFactor))
    )
    mergedBreakdown.deductions.forEach(
      comp => (comp.amount = this.round(comp.amount * lopProrationFactor))
    )

    return mergedBreakdown
  }

  private calculateProratedPortionForPeriod(
    salary: EmployeeSalary,
    activeDays: number,
    totalDays: number,
    payPeriodStartStr: string,
    payPeriodEndStr: string
  ): PayrollCalculationBreakdown {
    if (totalDays <= 0) return { earnings: [], deductions: [] }
    // Get the full month potential breakdown
    const fullMonthBreakdown = this.calculateMonthlySalaryComponents(
      salary,
      1, // No LOP factor here, it's applied later
      payPeriodStartStr,
      payPeriodEndStr
    )

    // Prorate each component's amount
    fullMonthBreakdown.earnings.forEach(
      comp => (comp.amount = this.round((comp.amount / totalDays) * activeDays))
    )
    fullMonthBreakdown.deductions.forEach(
      comp => (comp.amount = this.round((comp.amount / totalDays) * activeDays))
    )

    return fullMonthBreakdown
  }

  private calculateMonthlySalaryComponents(
    salary: EmployeeSalary,
    lopProrationFactor: number,
    payPeriodStartStr: string,
    payPeriodEndStr: string
  ): PayrollCalculationBreakdown {
    const earnings: SalaryComponent[] = []
    const deductions: SalaryComponent[] = []
    // const { salaryData, statutoryCompliances, effectiveFrom } = salary
    const { salaryData, statutoryCompliances, effectiveFrom } = salary
    const prorate = (amount: number) =>
      this.prorateComponentForEffectiveness(
        amount,
        effectiveFrom,
        payPeriodStartStr,
        payPeriodEndStr
      )
    // console.log('pay period start str', payPeriodStartStr)
    // console.log('pay period end str', payPeriodEndStr)

    // Helper to push to arrays
    const addEarning = (name: string, annualAmount: number) => {
      const amount = prorate(annualAmount / 12) * lopProrationFactor

      // const amount = this.round((annualAmount / 12) * lopProrationFactor)
      if (amount > 0) earnings.push({ name, amount })
    }

    const addDeduction = (name: string, annualAmount: number) => {
      //const amount = prorate(annualAmount / 12) * lopProrationFactor
      const amount = this.round((annualAmount / 12) * lopProrationFactor)
      if (amount > 0) deductions.push({ name, amount })
    }

    // Standard Components
    addEarning('Basic', salaryData.basic)
    addEarning('HRA', salaryData.hra)
    addEarning('Special Allowances', salaryData.specialAllowance)
    if (statutoryCompliances.pf) {
      addDeduction("Employee's PF Contribution", salaryData.pf)
    }
    // addDeduction('TDS', salaryData.tds ?? 0)
    // addDeduction('LOP', salaryData.lop ?? 0)
    // Other Dynamic Components
    salaryData.otherComponents?.forEach((comp: ExtendedSalaryComponent) => {
      const isInCTC = comp.info?.isInCTC || false
      const componentName = comp.name

      if (comp.employerContribution) {
        // const monthlyAmount = isInCTC ? comp.employerContribution / 12 : comp.employerContribution
        //const amount = (isInCTC ? prorate(monthlyAmount) : monthlyAmount) * lopProrationFactor
        // const amount = this.round(monthlyAmount * lopProrationFactor)
        const amount = isInCTC
          ? prorate(comp.employerContribution / 12) * lopProrationFactor
          : comp.employerContribution
        if (amount > 0) earnings.push({ name: componentName, amount })
      }
      if (comp.employeeContribution) {
        // console.log('employee contribution', comp.employeeContribution)
        // const monthlyAmount = isInCTC ? comp.employeeContribution / 12 : comp.employeeContribution
        //const amount = (isInCTC ? prorate(monthlyAmount) : monthlyAmount) * lopProrationFactor
        // const amount = this.round(monthlyAmount * lopProrationFactor)
        const amount = isInCTC
          ? prorate(comp.employeeContribution / 12) * lopProrationFactor
          : comp.employeeContribution
        if (amount > 0) deductions.push({ name: `${componentName} (Contribution)`, amount })
      }
    })

    // Round amounts to 2 decimal places
    earnings.forEach(e => (e.amount = parseFloat(e.amount.toFixed(2))))
    deductions.forEach(d => (d.amount = parseFloat(d.amount.toFixed(2))))
    return { earnings, deductions }
  }

  private mergeBreakdowns(
    ...breakdowns: PayrollCalculationBreakdown[]
  ): PayrollCalculationBreakdown {
    const finalBreakdown: PayrollCalculationBreakdown = { earnings: [], deductions: [] }
    const earningsMap = new Map<string, number>()
    const deductionsMap = new Map<string, number>()

    for (const breakdown of breakdowns) {
      breakdown.earnings.forEach(comp => {
        earningsMap.set(comp.name, (earningsMap.get(comp.name) || 0) + comp.amount)
      })
      breakdown.deductions.forEach(comp => {
        deductionsMap.set(comp.name, (deductionsMap.get(comp.name) || 0) + comp.amount)
      })
    }

    earningsMap.forEach((amount, name) =>
      finalBreakdown.earnings.push({ name, amount: this.round(amount) })
    )
    deductionsMap.forEach((amount, name) =>
      finalBreakdown.deductions.push({ name, amount: this.round(amount) })
    )

    return finalBreakdown
  }

  private prorateComponentForEffectiveness(
    fullMonthlyAmount: number,
    componentEffectiveFromStr: string,
    payPeriodStartStr: string,
    payPeriodEndStr: string
  ): number {
    const componentEffectiveFrom = new Date(componentEffectiveFromStr + 'T00:00:00Z')
    // console.log('effective from', componentEffectiveFrom)
    const payPeriodStartDate = new Date(payPeriodStartStr + 'T00:00:00Z')
    const payPeriodEndDate = new Date(payPeriodEndStr + 'T00:00:00Z')

    if (componentEffectiveFrom > payPeriodEndDate) return 0
    if (componentEffectiveFrom <= payPeriodStartDate) return fullMonthlyAmount

    const totalDaysInPayPeriod = getDaysInPeriod(payPeriodStartDate, payPeriodEndDate)
    if (totalDaysInPayPeriod <= 0) return 0

    const effectiveDaysInPeriod = getDaysInPeriod(componentEffectiveFrom, payPeriodEndDate)
    return parseFloat(
      ((fullMonthlyAmount / totalDaysInPayPeriod) * effectiveDaysInPeriod).toFixed(2)
    )
  }

  private calculateTds(grossSalary: number): number {
    if (grossSalary < this.monthlyTdsThreshold) return 0
    const tds = grossSalary * 0.05 // Example: 5% flat rate
    //return this.round(tds)
    return 0
  }
}
